import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Button,
  Box,
  Text,
  Flex,
  Input,
  HStack,
  Spinner,
  Badge,
  VStack,
  FormControl,
  FormLabel,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Icon,
  IconButton,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  useDisclosure,
  useRadio,
  useRadioGroup,
  UseRadioProps,
  Tooltip,
} from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Diamond,
  Medal,
  PencilSimple,
  Trash,
  Star,
  ListChecks,
  TextAa,
  ShieldCheck,
} from 'phosphor-react';
import { ICompany, NivelEmpresa, useCompanies } from '../../hooks/useCompanies';
import {
  useCompanyFields,
  ICompanyFieldValue,
  ICompanyFieldAssociation,
} from '../../hooks/useCompanyFields';
import { useAuth } from '../../providers/auth';
import { RegimeEspecialEmpresaPanel } from '../../pages/modulesHome/Supervisor/icms/regimeEspecial/components/RegimeEspecialEmpresaPanel';

interface EditCharacteristicsModalProps {
  isOpen: boolean;
  onClose: () => void;
  company: ICompany | null;
}

// Configuração dos níveis da empresa
const NIVEIS_EMPRESA_CONFIG: {
  value: NivelEmpresa;
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  icon: typeof Medal;
}[] = [
  {
    value: 'Latão',
    label: 'Latão',
    color: '#B87333',
    bgColor: 'orange.50',
    borderColor: 'orange.200',
    icon: Medal,
  },
  {
    value: 'Prata',
    label: 'Prata',
    color: '#A0AEC0',
    bgColor: 'gray.50',
    borderColor: 'gray.300',
    icon: Medal,
  },
  {
    value: 'Ouro',
    label: 'Ouro',
    color: '#D69E2E',
    bgColor: 'yellow.50',
    borderColor: 'yellow.400',
    icon: Medal,
  },
  {
    value: 'Diamante',
    label: 'Diamante',
    color: '#38B2AC',
    bgColor: 'cyan.50',
    borderColor: 'cyan.400',
    icon: Diamond,
  },
];

interface NivelEmpresaRadioCardProps extends UseRadioProps {
  children: React.ReactNode;
  nivelConfig: (typeof NIVEIS_EMPRESA_CONFIG)[0];
}

function NivelEmpresaRadioCard({ nivelConfig, children, ...props }: NivelEmpresaRadioCardProps) {
  const { getInputProps, getRadioProps } = useRadio(props);
  const input = getInputProps();
  const checkbox = getRadioProps();

  return (
    <Box as="label" flex={1} cursor="pointer">
      <input {...input} />
      <Box
        {...checkbox}
        borderWidth="2px"
        borderRadius="xl"
        borderColor={nivelConfig.borderColor}
        bg={nivelConfig.bgColor}
        px={3}
        py={2}
        textAlign="center"
        transition="all 0.2s"
        _checked={{
          borderColor: nivelConfig.color,
          bg: nivelConfig.bgColor,
          boxShadow: `0 0 0 2px ${nivelConfig.color}`,
          transform: 'scale(1.02)',
        }}
        _hover={{
          borderColor: nivelConfig.color,
          transform: 'scale(1.02)',
        }}
      >
        <VStack spacing={1}>
          <Icon as={nivelConfig.icon} boxSize={5} color={nivelConfig.color} weight="fill" />
          <Text fontSize="xs" fontWeight="bold" color={nivelConfig.color}>
            {children}
          </Text>
        </VStack>
      </Box>
    </Box>
  );
}

const GRUPO_ADMIN_ID = 1;

export function EditCharacteristicsModal({
  isOpen,
  onClose,
  company,
}: EditCharacteristicsModalProps) {
  const { user } = useAuth();
  // Verificação robusta: aceita tanto número quanto string
  const isAdmin =
    user && (Number(user.grupo_id) === GRUPO_ADMIN_ID || user.grupo_id === GRUPO_ADMIN_ID);
  const { updateNivelEmpresa } = useCompanies();
  const {
    getFields,
    getCompanyFieldValues,
    setCompanyFieldValue,
    deleteCompanyFieldValue,
    setCompanyField,
    getCompanyFieldAssociations,
    deleteCompanyFieldAssociation,
  } = useCompanyFields();
  const queryClient = useQueryClient();
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [savingFields, setSavingFields] = useState<Set<string>>(new Set());
  const [deletingField, setDeletingField] = useState<string | null>(null);
  const [valueToDelete, setValueToDelete] = useState<ICompanyFieldValue | null>(null);
  const [associationToDelete, setAssociationToDelete] = useState<ICompanyFieldAssociation | null>(
    null,
  );
  const [selectedNivel, setSelectedNivel] = useState<NivelEmpresa | ''>('');
  const [isUpdatingNivel, setIsUpdatingNivel] = useState(false);
  const [descriptionModalField, setDescriptionModalField] = useState<{
    id: number;
    campo: string;
  } | null>(null);

  const {
    isOpen: isDeleteAlertOpen,
    onOpen: onDeleteAlertOpen,
    onClose: onDeleteAlertClose,
  } = useDisclosure();

  const {
    isOpen: isDescriptionModalOpen,
    onOpen: onDescriptionModalOpen,
    onClose: onDescriptionModalClose,
  } = useDisclosure();

  const cancelRef = React.useRef<HTMLButtonElement>(null);

  const handleOpenDescriptionModal = (field: { id: number; campo: string }) => {
    setDescriptionModalField(field);
    onDescriptionModalOpen();
  };

  const handleCloseDescriptionModal = () => {
    setDescriptionModalField(null);
    onDescriptionModalClose();
  };

  const handleSaveDescription = async () => {
    if (!descriptionModalField) return;
    await handleSaveField(descriptionModalField.campo);
    handleCloseDescriptionModal();
  };

  useEffect(() => {
    if (company?.nivel) {
      setSelectedNivel(company.nivel);
    } else {
      setSelectedNivel('');
    }
  }, [company]);

  // Buscar campos disponíveis - apenas para admins
  const { data: fieldsData, isLoading: isLoadingFields } = useQuery({
    queryKey: ['companyFields'],
    queryFn: getFields,
    enabled: !!(isOpen && isAdmin),
  });

  // Buscar valores dos campos da empresa - apenas para admins
  const { data: valuesData, isLoading: isLoadingValues } = useQuery({
    queryKey: ['companyFieldValues', { companyId: company?.id }],
    queryFn: getCompanyFieldValues,
    enabled: !!(isOpen && !!company?.id && isAdmin),
  });

  // Buscar associações de campos do tipo select da empresa
  const { data: associationsData, isLoading: isLoadingAssociations } = useQuery({
    queryKey: ['companyFieldAssociations', { empresaId: company?.id }],
    queryFn: getCompanyFieldAssociations,
    enabled: !!(isOpen && !!company?.id && isAdmin),
  });

  const fields = Array.isArray(fieldsData?.data) ? fieldsData.data : [];
  const rawExistingValues = valuesData?.data;
  const existingValues = Array.isArray(rawExistingValues) ? rawExistingValues : [];
  const associations = Array.isArray(associationsData?.data) ? associationsData.data : [];

  // Helper para invalidar queries relacionadas a campos/valores
  const invalidateFieldQueries = async () => {
    // Invalida queries específicas da empresa
    await queryClient.invalidateQueries({
      queryKey: ['companyFieldValues', { companyId: company?.id }],
    });
    await queryClient.invalidateQueries({
      queryKey: ['companyFieldAssociations', { empresaId: company?.id }],
    });
    // Invalida queries de filtros e listagem de empresas
    await queryClient.invalidateQueries({ queryKey: ['CompaniesFilters'] });
    await queryClient.invalidateQueries({ queryKey: ['empresasListagemDash'] });
    await queryClient.invalidateQueries({ queryKey: ['companies'] });
    // Invalida queries de órfãos para atualizar a contagem no modal de limpeza
    await queryClient.refetchQueries({ queryKey: ['orphanValues'] });
    await queryClient.refetchQueries({ queryKey: ['orphanSets'] });
  };

  // Mutation para salvar valor
  const { mutateAsync: saveFieldValue } = useMutation({
    mutationFn: setCompanyFieldValue,
    onSuccess: invalidateFieldQueries,
  });

  // Mutation para deletar valor
  const { mutateAsync: deleteValueMutation } = useMutation({
    mutationFn: deleteCompanyFieldValue,
    onSuccess: invalidateFieldQueries,
  });

  // Mutation para atribuir campo do tipo select (sem valor)
  const { mutateAsync: setFieldMutation } = useMutation({
    mutationFn: setCompanyField,
    onSuccess: invalidateFieldQueries,
  });

  // Mutation para deletar associação de campo do tipo select
  const { mutateAsync: deleteAssociationMutation } = useMutation({
    mutationFn: deleteCompanyFieldAssociation,
    onSuccess: invalidateFieldQueries,
  });

  // Helper para extrair o nome do campo (pode vir como string ou objeto)
  const getCampoName = (campo: any): string => {
    if (typeof campo === 'string') return campo;
    if (campo && typeof campo === 'object' && typeof campo.campo === 'string') return campo.campo;
    return '';
  };

  // Inicializar valores quando os dados chegarem
  useEffect(() => {
    if (existingValues.length > 0) {
      const initialValues: Record<string, string> = {};
      existingValues.forEach(item => {
        const campoName = getCampoName(item.campo);
        if (campoName) {
          initialValues[campoName] = item.value;
        }
      });
      setFieldValues(initialValues);
    }
  }, [existingValues]);

  // Limpar estado ao fechar
  useEffect(() => {
    if (!isOpen) {
      setFieldValues({});
      setSavingFields(new Set());
      setDeletingField(null);
    }
  }, [isOpen]);

  const handleValueChange = (campo: string, value: string) => {
    setFieldValues(prev => ({ ...prev, [campo]: value }));
  };

  const getErrorMessage = (error: any, defaultMessage: string): string => {
    const detail = error.response?.data?.detail;
    if (!detail) return defaultMessage;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail.map((d: any) => d.msg || d.message || JSON.stringify(d)).join(', ');
    }
    if (typeof detail === 'object' && detail.msg) return detail.msg;
    return defaultMessage;
  };

  // Atribuir apenas o campo (sem valor) - para campos do tipo select
  const handleSetField = async (campoId: number, campoName: string) => {
    if (!company?.id) return;

    setSavingFields(prev => new Set(prev).add(campoName));

    try {
      await setFieldMutation({
        empresaId: company.id,
        campoId,
      });
      toast.success(`Campo atribuído com sucesso!`, {
        position: 'top-center',
        duration: 2000,
      });
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, 'Erro ao atribuir campo. Tente novamente.');
      toast.error(errorMessage, { position: 'top-right', duration: 3000 });
    } finally {
      setSavingFields(prev => {
        const next = new Set(prev);
        next.delete(campoName);
        return next;
      });
    }
  };

  // Salvar valor de um campo
  const handleSaveField = async (campo: string) => {
    if (!company?.id) return;

    setSavingFields(prev => new Set(prev).add(campo));

    try {
      await saveFieldValue({
        companyId: company.id,
        campo,
        value: fieldValues[campo] || '',
      });
      toast.success(`Valor salvo com sucesso!`, {
        position: 'top-center',
        duration: 2000,
      });
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, 'Erro ao salvar valor. Tente novamente.');
      toast.error(errorMessage, { position: 'top-right', duration: 3000 });
    } finally {
      setSavingFields(prev => {
        const next = new Set(prev);
        next.delete(campo);
        return next;
      });
    }
  };

  const handleSaveAll = async () => {
    if (!company?.id) return;

    // Filtrar campos com valores preenchidos
    const fieldsToSave = fields.filter(
      field => fieldValues[field.campo] !== undefined && fieldValues[field.campo] !== '',
    );

    if (fieldsToSave.length === 0) {
      toast.info('Nenhum campo para salvar.', { position: 'top-center', duration: 2000 });
      return;
    }

    const allFields = fieldsToSave.map(f => f.campo);
    setSavingFields(new Set(allFields));

    try {
      await Promise.all(
        fieldsToSave.map(field =>
          saveFieldValue({
            companyId: company.id,
            campo: field.campo,
            value: fieldValues[field.campo],
          }),
        ),
      );
      toast.success('Todos os valores foram salvos com sucesso!', {
        position: 'top-center',
        duration: 2000,
      });
      onClose();
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, 'Erro ao salvar valores. Tente novamente.');
      toast.error(errorMessage, { position: 'top-right', duration: 3000 });
    } finally {
      setSavingFields(new Set());
    }
  };

  const handleDeleteValueClick = (value: ICompanyFieldValue) => {
    setValueToDelete(value);
    onDeleteAlertOpen();
  };

  const handleConfirmDeleteValue = async () => {
    if (!valueToDelete || !company?.id) return;

    const campoName = getCampoName(valueToDelete.campo);
    setDeletingField(campoName);
    try {
      await deleteValueMutation({ companyId: company.id, campo: campoName });
      toast.success(`Valor removido com sucesso!`, {
        position: 'top-center',
        duration: 2000,
      });
      // Limpar o valor do estado local também
      setFieldValues(prev => {
        const next = { ...prev };
        delete next[campoName];
        return next;
      });
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, 'Erro ao remover valor. Tente novamente.');
      toast.error(errorMessage, { position: 'top-right', duration: 3000 });
    } finally {
      setDeletingField(null);
      setValueToDelete(null);
      onDeleteAlertClose();
    }
  };

  const handleDeleteAssociationClick = (association: ICompanyFieldAssociation) => {
    setAssociationToDelete(association);
    onDeleteAlertOpen();
  };

  const handleConfirmDeleteAssociation = async () => {
    if (!associationToDelete || !company?.id) return;

    const campoName = getCampoName(associationToDelete.campo);
    setDeletingField(campoName);
    try {
      await deleteAssociationMutation({
        empresaId: company.id,
        campoId: associationToDelete.campo.id,
      });
      toast.success(`Campo removido com sucesso!`, {
        position: 'top-center',
        duration: 2000,
      });
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, 'Erro ao remover campo. Tente novamente.');
      toast.error(errorMessage, { position: 'top-right', duration: 3000 });
    } finally {
      setDeletingField(null);
      setAssociationToDelete(null);
      onDeleteAlertClose();
    }
  };

  const formatFieldLabel = (campo: any): string => {
    const campoName = getCampoName(campo);
    if (!campoName) return '';
    return campoName.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
  };

  const isLoading = isLoadingFields || isLoadingValues || isLoadingAssociations;

  // Helper para obter nome do campo para delete alert
  const getDeleteCampoName = () => {
    if (valueToDelete) return getCampoName(valueToDelete.campo);
    if (associationToDelete) return getCampoName(associationToDelete.campo);
    return '';
  };

  // Helper para obter valor para delete alert
  const getDeleteValue = () => {
    if (valueToDelete) return valueToDelete.value;
    return null;
  };

  const handleChangeNivel = async (nivel: string) => {
    if (!company?.id) return;
    const novoNivel = nivel as NivelEmpresa;
    setIsUpdatingNivel(true);
    try {
      await updateNivelEmpresa({ companyId: company.id, nivel: novoNivel });
      setSelectedNivel(novoNivel);
      toast.success('Nível da empresa atualizado com sucesso!', {
        position: 'top-center',
        duration: 2000,
      });
      queryClient.invalidateQueries();
    } catch (error: any) {
      const errorMessage = getErrorMessage(
        error,
        'Erro ao atualizar nível da empresa. Tente novamente.',
      );
      toast.error(errorMessage, { position: 'top-right', duration: 3000 });
    } finally {
      setIsUpdatingNivel(false);
    }
  };

  const { getRootProps: getNivelRootProps, getRadioProps: getNivelRadioProps } = useRadioGroup({
    name: 'nivel_empresa',
    value: selectedNivel || '',
    onChange: handleChangeNivel,
  });

  const nivelEmpresaGroup = getNivelRootProps();

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent maxH="90vh">
          <ModalHeader pb={2}>
            <Flex direction="column" gap={1}>
              <Text fontSize="lg" fontWeight="bold">
                Editar Características
              </Text>
              {company && (
                <Text fontSize="sm" color="gray.500" fontWeight="normal">
                  {company.razao_social || company.nome}
                </Text>
              )}
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pt={0} pb={6}>
            {isLoading && (
              <Flex justify="center" align="center" py={8}>
                <Spinner size="lg" color="blue.500" />
                <Text ml={3} color="gray.500">
                  Carregando campos...
                </Text>
              </Flex>
            )}
            {!isLoading && !isAdmin && (
              <Box py={8} textAlign="center">
                <Text color="gray.500" fontSize="md" fontWeight="medium">
                  Acesso restrito. Apenas administradores podem editar características.
                </Text>
              </Box>
            )}
            {!isLoading && isAdmin && fields.length === 0 && (
              <Box py={8} textAlign="center">
                <Text color="gray.500">Nenhum campo disponível. Adicione campos primeiro.</Text>
              </Box>
            )}
            {!isLoading && isAdmin && (
              <Tabs isFitted variant="enclosed" colorScheme="blue">
                <TabList mb={4}>
                  {isAdmin && (
                    <Tab fontWeight="medium">
                      <Icon as={Star} mr={2} />
                      Nível da Empresa
                    </Tab>
                  )}
                  {isAdmin && (
                    <>
                      <Tab fontWeight="medium">
                        <Icon as={PencilSimple} mr={2} />
                        Editar Valores
                      </Tab>
                      <Tab fontWeight="medium">
                        <Icon as={Trash} mr={2} />
                        Apagar Valores
                      </Tab>
                      <Tab fontWeight="medium">
                        <Icon as={ShieldCheck} mr={2} />
                        Regime Especial
                      </Tab>
                    </>
                  )}
                </TabList>

                <TabPanels>
                  {/* Tab Nível da Empresa - Apenas Admin */}
                  {isAdmin && (
                    <TabPanel p={0}>
                      <VStack spacing={4} align="stretch">
                        <Box
                          p={4}
                          borderRadius="md"
                          border="1px"
                          borderColor="gray.200"
                          bg="gray.50"
                        >
                          <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={3}>
                            Selecione o nível da empresa
                          </Text>
                          <HStack {...nivelEmpresaGroup} spacing={3} align="stretch">
                            {NIVEIS_EMPRESA_CONFIG.map(nivelConfig => {
                              const radio = getNivelRadioProps({ value: nivelConfig.value });
                              return (
                                <NivelEmpresaRadioCard
                                  key={nivelConfig.value}
                                  nivelConfig={nivelConfig}
                                  {...radio}
                                >
                                  {nivelConfig.label}
                                </NivelEmpresaRadioCard>
                              );
                            })}
                          </HStack>
                          {isUpdatingNivel && (
                            <Flex align="center" mt={3} gap={2}>
                              <Spinner size="sm" color="blue.500" />
                              <Text fontSize="sm" color="gray.500">
                                Atualizando nível...
                              </Text>
                            </Flex>
                          )}
                          {!isUpdatingNivel && selectedNivel && (
                            <Text fontSize="xs" color="gray.500" mt={2}>
                              Nível atual: <strong>{selectedNivel}</strong>
                            </Text>
                          )}
                        </Box>
                      </VStack>
                    </TabPanel>
                  )}

                  {/* Tab Editar - Apenas Admin */}
                  {isAdmin && (
                    <TabPanel p={0}>
                      {fields.length === 0 ? (
                        <Box py={8} textAlign="center">
                          <Text color="gray.500">
                            Nenhum campo disponível. Adicione campos primeiro através do botão{' '}
                            &quot;Gerenciar Campos&quot;.
                          </Text>
                        </Box>
                      ) : (
                        <Tabs variant="soft-rounded" colorScheme="blue" size="sm">
                          <TabList mb={4}>
                            <Tab>
                              <Icon as={ListChecks} mr={2} />
                              Campos
                            </Tab>
                            <Tab>
                              <Icon as={TextAa} mr={2} />
                              Valores
                            </Tab>
                          </TabList>
                          <TabPanels>
                            {/* Sub-tab Campos - Atribuir campos do tipo select */}
                            <TabPanel p={0}>
                              <Text fontSize="sm" color="gray.600" mb={3}>
                                Atribua campos do tipo seleção à empresa. Opcionalmente adicione uma
                                descrição.
                              </Text>
                              <VStack spacing={3} align="stretch">
                                {fields
                                  .filter(f => f.tipo === 'select')
                                  .map(field => (
                                    <Flex
                                      key={field.campo}
                                      p={3}
                                      borderRadius="md"
                                      border="1px"
                                      borderColor="gray.200"
                                      bg="gray.50"
                                      justify="space-between"
                                      align="center"
                                    >
                                      <Box>
                                        <Text fontWeight="medium">
                                          {formatFieldLabel(field.campo)}
                                        </Text>
                                        <Badge colorScheme="blue" fontSize="xs">
                                          Seleção
                                        </Badge>
                                      </Box>
                                      <HStack spacing={2}>
                                        <Button
                                          colorScheme="blue"
                                          size="sm"
                                          onClick={() => handleSetField(field.id, field.campo)}
                                          isLoading={savingFields.has(field.campo)}
                                        >
                                          Atribuir
                                        </Button>
                                        <Tooltip label="Adicionar descrição/valor" placement="top">
                                          <IconButton
                                            aria-label="Adicionar descrição"
                                            icon={<Icon as={PencilSimple} />}
                                            size="sm"
                                            colorScheme="blue"
                                            variant="outline"
                                            borderRadius="full"
                                            onClick={() =>
                                              handleOpenDescriptionModal({
                                                id: field.id,
                                                campo: field.campo,
                                              })
                                            }
                                          />
                                        </Tooltip>
                                      </HStack>
                                    </Flex>
                                  ))}
                                {fields.filter(f => f.tipo === 'select').length === 0 && (
                                  <Box py={4} textAlign="center">
                                    <Text color="gray.500" fontSize="sm">
                                      Nenhum campo do tipo seleção disponível.
                                    </Text>
                                  </Box>
                                )}
                              </VStack>
                            </TabPanel>

                            {/* Sub-tab Valores - Atribuir valores aos campos de texto */}
                            <TabPanel p={0}>
                              <Text fontSize="sm" color="gray.600" mb={3}>
                                Atribua valores aos campos de texto da empresa.
                              </Text>
                              <VStack spacing={4} align="stretch">
                                {fields
                                  .filter(f => f.tipo === 'text')
                                  .map(field => (
                                    <Box
                                      key={field.campo}
                                      p={4}
                                      borderRadius="md"
                                      border="1px"
                                      borderColor="gray.200"
                                      bg="gray.50"
                                    >
                                      <FormControl>
                                        <Flex justify="space-between" align="center" mb={2}>
                                          <FormLabel mb={0} fontWeight="medium">
                                            {formatFieldLabel(field.campo)}
                                          </FormLabel>
                                          <Badge colorScheme="green" fontSize="xs">
                                            Texto
                                          </Badge>
                                        </Flex>
                                        <Flex gap={2}>
                                          <Input
                                            value={fieldValues[field.campo] || ''}
                                            onChange={e =>
                                              handleValueChange(field.campo, e.target.value)
                                            }
                                            placeholder={`Digite o valor para ${formatFieldLabel(field.campo)}`}
                                            bg="white"
                                            flex={1}
                                          />
                                          <Button
                                            colorScheme="blue"
                                            size="md"
                                            onClick={() => handleSaveField(field.campo)}
                                            isLoading={savingFields.has(field.campo)}
                                            isDisabled={!fieldValues[field.campo]}
                                          >
                                            Salvar
                                          </Button>
                                        </Flex>
                                      </FormControl>
                                    </Box>
                                  ))}
                                {fields.filter(f => f.tipo === 'text').length === 0 && (
                                  <Box py={4} textAlign="center">
                                    <Text color="gray.500" fontSize="sm">
                                      Nenhum campo do tipo texto disponível.
                                    </Text>
                                  </Box>
                                )}
                              </VStack>
                            </TabPanel>
                          </TabPanels>
                        </Tabs>
                      )}
                    </TabPanel>
                  )}

                  {/* Tab Apagar - Apenas Admin */}
                  {isAdmin && (
                    <TabPanel p={0}>
                      {associations.length === 0 && existingValues.length === 0 ? (
                        <Box py={8} textAlign="center">
                          <Text color="gray.500">
                            Nenhum campo ou valor cadastrado para esta empresa.
                          </Text>
                        </Box>
                      ) : (
                        <Tabs variant="soft-rounded" colorScheme="red" size="sm">
                          <TabList mb={4}>
                            <Tab>
                              <Icon as={ListChecks} mr={2} />
                              Campos
                            </Tab>
                            <Tab>
                              <Icon as={TextAa} mr={2} />
                              Valores
                            </Tab>
                          </TabList>
                          <TabPanels>
                            {/* Sub-tab Campos - Remover associação de campos select */}
                            <TabPanel p={0}>
                              <Text fontSize="sm" color="gray.600" mb={3}>
                                Remova a associação de campos do tipo seleção desta empresa.
                              </Text>
                              <VStack spacing={3} align="stretch">
                                {associations.length > 0 ? (
                                  associations.map(association => {
                                    const campoName = getCampoName(association.campo);
                                    return (
                                      <Flex
                                        key={association.id}
                                        p={3}
                                        borderRadius="md"
                                        border="1px"
                                        borderColor="gray.200"
                                        bg="white"
                                        justify="space-between"
                                        align="center"
                                      >
                                        <Box>
                                          <Text fontWeight="medium">
                                            {formatFieldLabel(association.campo)}
                                          </Text>
                                          <Badge colorScheme="blue" fontSize="xs">
                                            Seleção
                                          </Badge>
                                        </Box>
                                        <IconButton
                                          aria-label="Remover campo"
                                          icon={<Icon as={Trash} />}
                                          size="sm"
                                          colorScheme="red"
                                          variant="ghost"
                                          onClick={() => handleDeleteAssociationClick(association)}
                                          isLoading={deletingField === campoName}
                                        />
                                      </Flex>
                                    );
                                  })
                                ) : (
                                  <Box py={4} textAlign="center">
                                    <Text color="gray.500" fontSize="sm">
                                      Nenhum campo do tipo seleção associado.
                                    </Text>
                                  </Box>
                                )}
                              </VStack>
                            </TabPanel>

                            {/* Sub-tab Valores - Remover valores de campos */}
                            <TabPanel p={0}>
                              <Text fontSize="sm" color="gray.600" mb={3}>
                                Remova valores de campos desta empresa.
                              </Text>
                              <VStack spacing={3} align="stretch">
                                {existingValues.length > 0 ? (
                                  existingValues
                                    .filter(value => value && getCampoName(value.campo))
                                    .map(value => {
                                      const campoName = getCampoName(value.campo);
                                      const { campo } = value;
                                      const isSelect =
                                        typeof campo === 'object' && campo?.tipo === 'select';
                                      return (
                                        <Flex
                                          key={campoName}
                                          p={3}
                                          borderRadius="md"
                                          border="1px"
                                          borderColor="gray.200"
                                          bg="white"
                                          justify="space-between"
                                          align="center"
                                        >
                                          <Box>
                                            <Text fontWeight="medium">
                                              {formatFieldLabel(value.campo)}
                                            </Text>
                                            <Text fontSize="sm" color="gray.600">
                                              Valor: {String(value.value ?? '')}
                                            </Text>
                                            <Badge
                                              colorScheme={isSelect ? 'blue' : 'green'}
                                              fontSize="xs"
                                            >
                                              {isSelect ? 'Seleção' : 'Texto'}
                                            </Badge>
                                          </Box>
                                          <IconButton
                                            aria-label="Remover valor"
                                            icon={<Icon as={Trash} />}
                                            size="sm"
                                            colorScheme="red"
                                            variant="ghost"
                                            onClick={() => handleDeleteValueClick(value)}
                                            isLoading={deletingField === campoName}
                                          />
                                        </Flex>
                                      );
                                    })
                                ) : (
                                  <Box py={4} textAlign="center">
                                    <Text color="gray.500" fontSize="sm">
                                      Nenhum valor cadastrado para esta empresa.
                                    </Text>
                                  </Box>
                                )}
                              </VStack>
                            </TabPanel>
                          </TabPanels>
                        </Tabs>
                      )}
                    </TabPanel>
                  )}

                  {/* Tab Regime Especial - Apenas Admin */}
                  {isAdmin && (
                    <TabPanel p={0}>
                      {company ? (
                        <RegimeEspecialEmpresaPanel empresaId={company.id} />
                      ) : (
                        <Box py={4} textAlign="center">
                          <Text color="gray.500" fontSize="sm">
                            Selecione uma empresa para gerenciar regimes especiais.
                          </Text>
                        </Box>
                      )}
                    </TabPanel>
                  )}
                </TabPanels>
              </Tabs>
            )}
          </ModalBody>
          <Divider />
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            {isAdmin && (
              <Button
                colorScheme="blue"
                onClick={handleSaveAll}
                isLoading={savingFields.size > 0}
                isDisabled={fields.length === 0}
              >
                Salvar Todos
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Alert de Confirmação de Delete */}
      <AlertDialog
        isOpen={isDeleteAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => {
          onDeleteAlertClose();
          setValueToDelete(null);
          setAssociationToDelete(null);
        }}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {associationToDelete ? 'Remover Campo' : 'Remover Valor'}
            </AlertDialogHeader>
            <AlertDialogBody>
              {associationToDelete ? (
                <>
                  Tem certeza que deseja remover a associação do campo &quot;
                  {getDeleteCampoName()}&quot; desta empresa?
                </>
              ) : (
                <>
                  Tem certeza que deseja remover o valor &quot;{getDeleteValue()}&quot; do campo
                  &quot;{getDeleteCampoName()}&quot;?
                </>
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() => {
                  onDeleteAlertClose();
                  setValueToDelete(null);
                  setAssociationToDelete(null);
                }}
              >
                Cancelar
              </Button>
              <Button
                colorScheme="red"
                onClick={
                  associationToDelete ? handleConfirmDeleteAssociation : handleConfirmDeleteValue
                }
                ml={3}
                isLoading={deletingField !== null}
              >
                Remover
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Modal de Descrição/Valor */}
      <Modal isOpen={isDescriptionModalOpen} onClose={handleCloseDescriptionModal} isCentered>
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent>
          <ModalHeader>
            {descriptionModalField ? formatFieldLabel(descriptionModalField.campo) : ''}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FormControl>
              <FormLabel fontSize="sm" color="gray.600">
                Descrição/Valor
              </FormLabel>
              <Input
                placeholder="Digite aqui..."
                value={descriptionModalField ? fieldValues[descriptionModalField.campo] || '' : ''}
                onChange={e => {
                  if (descriptionModalField) {
                    handleValueChange(descriptionModalField.campo, e.target.value);
                  }
                }}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={handleCloseDescriptionModal}>
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSaveDescription}
              isLoading={
                descriptionModalField ? savingFields.has(descriptionModalField.campo) : false
              }
              isDisabled={!descriptionModalField || !fieldValues[descriptionModalField.campo]}
            >
              Salvar com Descrição
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
